import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Calendar, Users, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from './Toast';
import { getEndpointUrl, getApiUrl } from '../config/api';
import API_CONFIG from '../config/api';
import './Cart.css';

const Cart = ({ cartItems, removeFromCart, clearCart, user }) => {  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [bookingType, setBookingType] = useState(null); // 'online' or 'pay-at-hotel'
  const [completedBookings, setCompletedBookings] = useState([]); // Store booking details for success message
  const [isTestMode, setIsTestMode] = useState(true); // Will be set based on Razorpay key
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityErrors, setAvailabilityErrors] = useState([]);
  const [showAvailabilityAlert, setShowAvailabilityAlert] = useState(false);

  // Format date for display (dd/mm/yyyy)
  const formatDateForDisplay = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return dateString; // Return original if formatting fails
    }
  };
  // Kept for backward compatibility but now using calculateTotalBreakdown
  // eslint-disable-next-line no-unused-vars
  const getTotalAmount = () => {
    return cartItems.reduce((total, item) => total + item.totalPrice, 0);
  };

  // Calculate individual item breakdown for display
  const calculateItemBreakdown = (item) => {
    if (!item || !item.nights) return { subtotal: 0, gst: 0, total: 0 };
    
    // Get base price based on occupancy type - fix the comparison
    const basePrice = item.occupancyType === 'single' ? 
      (item.room.singleOccupancyPrice || item.room.price) : 
      (item.room.doubleOccupancyPrice || item.room.price);
    
    // Calculate extra bed charges
    const extraBedPrice = (item.room.extraBedPrice || 0) * (item.extraBedCount || 0);
    
    // Calculate breakfast charges - use occupancy-based calculation like in Rooms.js
    const breakfastPeople = item.occupancyType === 'single' ? 1 : 2;
    const breakfastPrice = item.includeBreakfast ? (item.room.breakfastPrice || 0) * breakfastPeople : 0;
    
    // Calculate per night price
    const perNightPrice = basePrice + extraBedPrice + breakfastPrice;
    
    // Calculate total before GST
    const subtotal = perNightPrice * item.nights;
    
    // Calculate GST (12% for hospitality)
    const gst = subtotal * 0.12;
    
    // Total with GST
    const total = subtotal + gst;
    
    return {
      basePrice,
      extraBedPrice,
      breakfastPrice,
      breakfastPeople, // Add this for display
      perNightPrice,
      subtotal: Math.round(subtotal * 100) / 100,
      gst: Math.round(gst * 100) / 100,
      total: Math.round(total * 100) / 100
    };
  };

  // Calculate total breakdown for all items
  const calculateTotalBreakdown = () => {
    const totalSubtotal = cartItems.reduce((total, item) => {
      const breakdown = calculateItemBreakdown(item);
      return total + breakdown.subtotal;
    }, 0);
    
    const totalGst = totalSubtotal * 0.12;
    const grandTotal = totalSubtotal + totalGst;
    
    return {
      subtotal: Math.round(totalSubtotal * 100) / 100,
      gst: Math.round(totalGst * 100) / 100,
      total: Math.round(grandTotal * 100) / 100
    };
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      // Check if Razorpay is already loaded
      if (window.Razorpay) {
        console.log('✅ Razorpay already loaded');
        resolve(true);
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        console.log('⏳ Razorpay script already being loaded, waiting...');
        existingScript.onload = () => {
          console.log('✅ Existing Razorpay script loaded');
          resolve(true);
        };
        existingScript.onerror = () => {
          console.error('❌ Existing Razorpay script failed to load');
          resolve(false);
        };
        return;
      }

      console.log('📦 Loading Razorpay script...');
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      
      script.onload = () => {
        console.log('✅ Razorpay script loaded successfully');
        // Wait a bit for Razorpay to be available
        setTimeout(() => {
          if (window.Razorpay) {
            console.log('✅ Razorpay is now available');
            resolve(true);
          } else {
            console.error('❌ Razorpay script loaded but Razorpay object not available');
            resolve(false);
          }
        }, 100);
      };
      
      script.onerror = () => {
        console.error('❌ Failed to load Razorpay script');
        resolve(false);
      };
      
      document.head.appendChild(script);
    });
  };

  // Notify admin about failed booking attempts
  const notifyAdminOfFailedBooking = async (item, errorMessage) => {
    try {
      const token = localStorage.getItem('token');
      const notificationData = {
        type: 'FAILED_BOOKING_ATTEMPT',
        userId: user?.id,
        userEmail: user?.email,
        userName: user?.name,
        roomName: item.room.name,
        checkInDate: item.checkInDate,
        checkOutDate: item.checkOutDate,
        guests: item.guests,
        totalPrice: item.totalPrice,
        errorMessage: errorMessage,
        timestamp: new Date().toISOString()
      };

      await fetch(getEndpointUrl('ADMIN', 'NOTIFICATIONS'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(notificationData)
      });
    } catch (error) {
      console.error('Failed to notify admin:', error);
    }
  };  const createBookings = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Please login to complete booking');
    }    const createdBookings = [];
    
    // First, verify all rooms are still available before creating any bookings
    const unavailableRooms = [];    for (const item of cartItems) {
      try {
        // Use externalId if available (for DynamoDB rooms), otherwise use regular ID
        let actualRoomId = item.room.externalId || item.room.id.toString();        // Check real-time availability
        const availabilityUrl = `${getApiUrl('/api/rooms/availability')}?roomId=${encodeURIComponent(actualRoomId)}&checkIn=${new Date(item.checkInDate).toISOString().split('T')[0]}&checkOut=${new Date(item.checkOutDate).toISOString().split('T')[0]}`;
        
        const availabilityResponse = await fetch(availabilityUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (availabilityResponse.ok) {
          const availability = await availabilityResponse.json();
          if (!availability.available) {
            unavailableRooms.push({
              roomName: item.room.name,
              checkIn: item.checkInDate,
              checkOut: item.checkOutDate
            });
          }
        } else {
          // If availability check fails, assume room might not be available
          console.warn('Could not verify availability for room:', item.room.name);
        }
      } catch (error) {
        console.error('Error checking availability for room:', item.room.name, error);
      }
    }    // If any rooms are unavailable, show specific error message
    if (unavailableRooms.length > 0) {
      const roomNames = unavailableRooms.map(room => room.roomName).join(', ');
      const message = unavailableRooms.length === 1 
        ? `Sorry, ${roomNames} is no longer available for your selected dates. Please choose different dates or remove it from your cart.`
        : `Sorry, these rooms are no longer available for your selected dates: ${roomNames}. Please choose different dates or remove them from your cart.`;
      
      // Set prominent error display
      setAvailabilityErrors(unavailableRooms);
      setShowAvailabilityAlert(true);
      
      toast.error(message);
      throw new Error(message);
    }    // Process each cart item as a separate booking
    for (const item of cartItems) {
      // Use externalId if available (for DynamoDB rooms), otherwise use regular ID
      let actualRoomId = item.room.externalId || item.room.id.toString();
        // Calculate total guests based on occupancy type and extra beds
        const baseGuests = item.occupancyType === 'single' ? 1 : 2;
        const totalGuests = baseGuests + (item.extraBedCount || 0);
        
        const bookingRequest = {
        roomId: actualRoomId, // Use externalId or regular ID
        checkInDate: new Date(item.checkInDate).toISOString().split('T')[0],
        checkOutDate: new Date(item.checkOutDate).toISOString().split('T')[0],
        guests: totalGuests, // Calculate based on occupancy + extra beds
        occupancyType: (item.occupancyType || 'single').toUpperCase(), // Convert to uppercase for backend
        extraBed: (item.extraBedCount || 0) > 0, // Set extraBed to true if extraBedCount > 0
        extraBedCount: item.extraBedCount || 0,
        breakfast: item.includeBreakfast || false,
        breakfastCount: item.includeBreakfast ? (item.occupancyType === 'single' ? 1 : 2) : 0,
        // Skip individual notifications when creating multiple bookings
        skipNotifications: cartItems.length > 1
      };      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.BOOKINGS.CREATE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookingRequest)
      });      if (!response.ok) {
        let errorMessage = 'Unknown error';
        try {
          const error = await response.json();
          errorMessage = error.message || error.error || 'Unknown error';
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || `Server error (${response.status})`;
        }
        
        console.error('❌ Booking creation failed:', errorMessage);
        
        // Handle specific error cases with user-friendly messages
        if (errorMessage.toLowerCase().includes('not available')) {
          toast.error(`🚫 ${item.room.name} is no longer available for ${item.checkInDate} to ${item.checkOutDate}. Please remove it from your cart and try again.`);
        } else if (errorMessage.toLowerCase().includes('conflicting')) {
          toast.error(`📅 There's a scheduling conflict for ${item.room.name}. Please choose different dates.`);
        } else if (errorMessage.toLowerCase().includes('unauthorized') || response.status === 401) {
          toast.error('🔐 Your session has expired. Please login again.');
        } else if (errorMessage.toLowerCase().includes('forbidden') || response.status === 403) {
          toast.error('🚫 You don\'t have permission to make this booking.');
        } else if (response.status >= 500) {
          toast.error('🔧 Server error occurred. Please try again later or contact support.');
        } else {
          toast.error(`❌ Failed to book ${item.room.name}: ${errorMessage}`);
        }
        
        // Notify admin about failed booking attempt
        await notifyAdminOfFailedBooking(item, errorMessage);
        throw new Error(errorMessage);
      }const booking = await response.json();
      createdBookings.push(booking);
    }

    return createdBookings;
  };  const createCombinedPaymentOrder = async (bookings) => {
    const token = localStorage.getItem('token');
    
    // Extract booking IDs
    const bookingIds = bookings.map(booking => booking.id);
    
    console.log('Creating combined payment order for bookings:', bookingIds);
    
    const response = await fetch(`${getApiUrl('/api/payments/create-order/multiple')}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        bookingIds: bookingIds
      })
    });

    if (!response.ok) {
      let errorMessage = 'Failed to create combined payment order';
      try {
        const error = await response.json();
        errorMessage = error.message || error.error || errorMessage;
      } catch (e) {
        errorMessage = `Combined payment order creation failed (${response.status})`;
      }
      
      console.error('❌ Combined payment order creation failed:', errorMessage);
      toast.error('❌ Failed to create combined payment order: ' + errorMessage);
      throw new Error(errorMessage);
    }

    const paymentOrder = await response.json();
    console.log('✅ Combined payment order created:', paymentOrder);
    
    // Add bookings reference to the payment order
    return { ...paymentOrder, bookings };
  };

  const handleRazorpayPayment = async (paymentOrder) => {
    console.log('💳 Starting Razorpay payment with order:', paymentOrder);
    
    return new Promise((resolve, reject) => {
      const options = {
        key: paymentOrder.keyId,
        amount: paymentOrder.amount,
        currency: paymentOrder.currency,
        name: 'The Container Hotel',
        description: paymentOrder.description || `Payment for ${paymentOrder.totalBookings || 1} booking(s)`,
        order_id: paymentOrder.orderId,
        prefill: {
          name: paymentOrder.customerName,
          email: paymentOrder.customerEmail,
        },        theme: {
          color: '#2563eb'
        },
        // Enhanced payment method configuration for Indian market
        config: {
          display: {
            blocks: {
              utib: { // UPI
                name: 'Pay using UPI',
                instruments: [
                  { method: 'upi' }
                ]
              },
              banks: { // Net Banking
                name: 'Net Banking',
                instruments: [
                  { method: 'netbanking' }
                ]
              },
              other: { // Cards and Wallets
                name: 'Other Payment Methods',
                instruments: [
                  { method: 'card' },
                  { method: 'wallet' }
                ]
              }
            },
            sequence: ['block.utib', 'block.banks', 'block.other'],
            preferences: {
              show_default_blocks: true
            }
          }
        },
        // Restrict to domestic methods to avoid international card issues
        method: {
          netbanking: true,
          card: {
            type: ['debit', 'credit'],
            issuer: ['HDFC', 'ICICI', 'SBI', 'AXIS', 'KOTAK', 'YES', 'PNB', 'BOB', 'CANARA', 'UNION', 'IDBI', 'FEDERAL', 'INDUSIND', 'CITI']
          },
          upi: true,
          wallet: {
            phonepe: true,
            paytm: true,
            mobikwik: true,
            amazonpay: true,
            freecharge: true,
            olamoney: true,
            jiomoney: true
          },
          paylater: true
        },        handler: async function (response) {
          try {
            console.log('💳 Payment successful, verifying with backend...');
            console.log('Payment response:', response);
            
            // Verify payment with backend
            const verifyResponse = await fetch(getApiUrl('/api/payments/verify'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            if (!verifyResponse.ok) {
              console.error('❌ Payment verification failed:', verifyResponse.status);
              let errorMessage = 'Payment verification failed';
              try {
                const errorData = await verifyResponse.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
              } catch (e) {
                console.error('Failed to parse verification error response');
              }
              toast.error('Payment verification failed: ' + errorMessage);
              throw new Error(errorMessage);
            }            const verifyResult = await verifyResponse.json();
            console.log('✅ Payment verified successfully:', verifyResult);
            toast.success('Payment completed successfully!');
            resolve(verifyResult);
          } catch (error) {
            console.error('❌ Payment verification error:', error);
            toast.error('Payment verification failed: ' + error.message);
            reject(error);
          }
        },        modal: {
          ondismiss: function() {
            console.log('❌ Payment modal dismissed by user');
            toast.info('Payment was cancelled by user');
            reject(new Error('Payment was cancelled'));
          }        }
      };
        // Detect test mode based on key
      const isTest = options.key.startsWith('rzp_test_');
      setIsTestMode(isTest);
      
      console.log('💳 Razorpay options:', options);
      console.log('🧪 Test mode:', isTest);
      
      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        console.error('❌ Razorpay script not loaded');
        reject(new Error('Razorpay script not loaded'));
        return;
      }

      console.log('🚀 Creating Razorpay instance...');
      const rzp = new window.Razorpay(options);
      
      console.log('✅ Razorpay instance created successfully');      // Set up payment failure handler before opening the modal
      rzp.on('payment.failed', function (response) {
        console.error('❌ Payment failed in Razorpay:', response.error);
        
        const errorCode = response.error.code;
        const errorDescription = response.error.description;
        const errorReason = response.error.reason;
        
        console.log('Error details:', { errorCode, errorDescription, errorReason });
        
        // Handle specific error types with user-friendly messages
        let userMessage = '';
        
        if (errorCode === 'BAD_REQUEST_ERROR') {
          if (errorDescription && errorDescription.toLowerCase().includes('international')) {
            userMessage = '🚫 International cards are not supported. Please use an Indian debit/credit card, UPI, or net banking.';
          } else if (errorDescription && errorDescription.toLowerCase().includes('card not supported')) {
            userMessage = '🚫 This card is not supported. Please try with UPI, net banking, or a different card.';
          } else {
            userMessage = '🚫 Invalid payment details. Please check your information and try again.';
          }
        } else if (errorCode === 'GATEWAY_ERROR') {
          userMessage = '🏦 Payment gateway error. Please try with a different payment method or try again later.';
        } else if (errorCode === 'NETWORK_ERROR') {
          userMessage = '🌐 Network error. Please check your internet connection and try again.';
        } else if (errorDescription && errorDescription.toLowerCase().includes('insufficient')) {
          userMessage = '💰 Insufficient funds. Please try with a different card or payment method.';
        } else if (errorDescription && errorDescription.toLowerCase().includes('declined')) {
          userMessage = '❌ Payment declined by bank. Please try with a different card or contact your bank.';
        } else if (errorDescription && errorDescription.toLowerCase().includes('expired')) {
          userMessage = '⏰ Card or payment session expired. Please try again.';
        } else if (errorDescription && errorDescription.toLowerCase().includes('limit')) {
          userMessage = '🚫 Transaction limit exceeded. Please try with a different amount or payment method.';
        } else {
          userMessage = `❌ Payment failed: ${errorDescription || errorReason || 'Unknown error occurred'}`;
        }
          // Show user-friendly error message
        toast.error(userMessage);
        
        // Scroll to top to ensure user sees the error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Report failure to backend for logging
        fetch(getApiUrl('/api/payments/failure'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            razorpay_order_id: paymentOrder.orderId,
            error_code: errorCode,
            error_description: errorDescription,
            error_reason: errorReason
          })
        }).catch(err => console.error('Failed to report payment failure to backend:', err));

        reject(new Error(errorDescription || errorReason || 'Payment failed'));
      });

      // Open the Razorpay modal with error handling
      console.log('🎯 Opening Razorpay payment modal...');
      try {
        rzp.open();
        console.log('✅ Razorpay modal opened successfully');
      } catch (error) {
        console.error('❌ Failed to open Razorpay modal:', error);
        toast.error('Failed to open payment modal: ' + error.message);
        reject(new Error('Failed to open payment modal: ' + error.message));
        return;
      }
    });
  };  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    console.log('🚀 Starting checkout process...');
    console.log('Cart items:', cartItems);
    console.log('User:', user);

    setIsProcessing(true);
    setPaymentStatus(null);
    setShowAvailabilityAlert(false); // Clear any availability alerts
    setAvailabilityErrors([]);
    
    try {
      // Load Razorpay script
      console.log('📦 Loading Razorpay script...');
      const scriptLoaded = await loadRazorpayScript();
      console.log('📦 Razorpay script loaded:', scriptLoaded);
      
      if (!scriptLoaded) {
        toast.error('❌ Failed to load payment gateway. Please refresh the page and try again.');
        throw new Error('Failed to load payment gateway. Please try again.');
      }
      
      // Double-check if Razorpay is available with retries
      let retries = 3;
      while (retries > 0 && !window.Razorpay) {
        console.log(`⏳ Waiting for Razorpay to be available... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, 500));
        retries--;
      }
      
      if (!window.Razorpay) {
        console.error('❌ Razorpay not found in window object after retries');
        toast.error('❌ Payment gateway not available. Please refresh the page and try again.');
        throw new Error('Payment gateway not available. Please refresh and try again.');
      }
      
      console.log('✅ Razorpay is available:', typeof window.Razorpay);      // Step 1: Create bookings
      console.log('🏨 Creating bookings...');
      const createdBookings = await createBookings();
      console.log('🏨 Created bookings:', createdBookings);

      // Step 2: Create a single combined payment order for all bookings
      console.log('💳 Creating combined payment order...');
      const combinedPaymentOrder = await createCombinedPaymentOrder(createdBookings);
      console.log('💳 Combined payment order created:', combinedPaymentOrder);

      // Step 3: Process single payment for all bookings
      console.log('⚡ Processing combined payment...');
      try {
        console.log('💰 Processing combined payment for all bookings');
        const result = await handleRazorpayPayment(combinedPaymentOrder);
        console.log('✅ Combined payment successful:', result);
        
        setPaymentStatus('success');
        setBookingType('online');
        setCompletedBookings(createdBookings);
        clearCart();
        
        // Enhanced success message with booking details
        const bookingDetails = createdBookings.map(booking => 
          `${booking.roomName || booking.room?.name || 'Room'} (${booking.checkInDate} to ${booking.checkOutDate})`
        ).join(', ');
        
        const successMessage = (
          <div>
            <div>🎉 Payment successful! {createdBookings.length} booking(s) confirmed:</div>
            <div style={{ fontSize: '0.9em', marginTop: '4px', opacity: '0.9' }}>{bookingDetails}</div>
            <div style={{ marginTop: '8px' }}>
              <Link to="/profile" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                View your bookings in Profile →
              </Link>
            </div>
          </div>
        );
        toast.success(successMessage);

        // Send consolidated notification if multiple bookings
        if (createdBookings.length > 1) {
          console.log('📱 Sending consolidated notification for multiple online bookings...');
          await sendConsolidatedNotification(createdBookings, 'online');
        }
      } catch (error) {
        console.error('❌ Combined payment failed:', error);
        setPaymentStatus('failed');
        toast.error('❌ Payment failed. Please try again or contact support.');
        // Scroll to top to ensure user sees the error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }    } catch (error) {
      console.error('❌ Checkout error:', error);
      setPaymentStatus('failed');
      
      // Scroll to top to ensure user sees the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Show specific error messages based on error type
      if (error.message.includes('load payment gateway')) {
        toast.error('❌ Failed to load payment gateway. Please refresh the page and try again.');
      } else if (error.message.includes('gateway not available')) {
        toast.error('❌ Payment gateway not available. Please refresh the page and try again.');
      } else if (error.message.includes('not available')) {
        // Room availability error - already handled above
      } else if (error.message.includes('login')) {
        toast.error('❌ Please login to complete your booking.');
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        toast.error('❌ Network error. Please check your connection and try again.');
      } else {
        toast.error('❌ Booking failed: ' + error.message);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle Pay At Hotel option
  const handlePayAtHotel = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!user) {
      toast.error('Please login to proceed with booking');
      return;
    }

    console.log('🏨 Starting Pay At Hotel process...');
    setIsProcessing(true);
    setPaymentStatus(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please login to complete booking');
      }

      const createdBookings = [];
      
      // Create bookings for each cart item
      for (const item of cartItems) {
        // Use externalId if available (for DynamoDB rooms), otherwise use regular ID
        let actualRoomId = item.room.externalId || item.room.id.toString();
        
        // Calculate total guests based on occupancy type and extra beds
        const baseGuests = item.occupancyType === 'single' ? 1 : 2;
        const totalGuests = baseGuests + (item.extraBedCount || 0);
        
        const bookingRequest = {
          roomId: actualRoomId,
          checkInDate: new Date(item.checkInDate).toISOString().split('T')[0],
          checkOutDate: new Date(item.checkOutDate).toISOString().split('T')[0],
          guests: totalGuests,
          occupancyType: (item.occupancyType || 'single').toUpperCase(),
          extraBed: (item.extraBedCount || 0) > 0,
          extraBedCount: item.extraBedCount || 0,
          breakfast: item.includeBreakfast || false,
          breakfastCount: item.includeBreakfast ? (item.occupancyType === 'single' ? 1 : 2) : 0,
          // Skip individual notifications when creating multiple bookings
          skipNotifications: cartItems.length > 1
        };

        console.log(`Creating pay-at-hotel booking for ${item.room.name}:`, bookingRequest);

        const response = await fetch(getApiUrl('/api/bookings/pay-at-hotel'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(bookingRequest)
        });

        if (!response.ok) {
          let errorMessage = 'Unknown error';
          try {
            const error = await response.json();
            errorMessage = error.message || error.error || 'Unknown error';
          } catch (e) {
            errorMessage = response.statusText || `Server error (${response.status})`;
          }
          
          console.error('❌ Pay-at-hotel booking creation failed:', errorMessage);
          throw new Error(`Failed to create pay-at-hotel booking for ${item.room.name}: ${errorMessage}`);
        }

        const booking = await response.json();
        createdBookings.push(booking);
        console.log(`✅ Pay-at-hotel booking created for ${item.room.name}:`, booking);
      }
      
      setPaymentStatus('success');
      setBookingType('pay-at-hotel');
      setCompletedBookings(createdBookings);
      clearCart();
      
      // Enhanced success message with booking details
      const bookingDetails = createdBookings.map(booking => 
        `${booking.roomName || booking.room?.name || 'Room'} (${booking.checkInDate} to ${booking.checkOutDate})`
      ).join(', ');
      
      const successMessage = (
        <div>
          <div>🏨 Pay-at-hotel booking{createdBookings.length > 1 ? 's' : ''} submitted successfully!</div>
          <div style={{ fontSize: '0.9em', marginTop: '4px', opacity: '0.9' }}>{bookingDetails}</div>
          <div style={{ marginTop: '4px' }}>You will receive confirmation via email once approved by hotel admin.</div>
          <div style={{ marginTop: '8px' }}>
            <Link to="/profile" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
              Track your bookings in Profile →
            </Link>
          </div>
        </div>
      );
      toast.success(successMessage);
      
      console.log('✅ All pay-at-hotel bookings created:', createdBookings);

      // Send consolidated notification if multiple bookings
      if (createdBookings.length > 1) {
        console.log('📱 Sending consolidated notification for multiple pay-at-hotel bookings...');
        await sendConsolidatedNotification(createdBookings, 'pay-at-hotel');
      }

    } catch (error) {
      console.error('❌ Pay At Hotel error:', error);
      setPaymentStatus('failed');
      toast.error('❌ Booking request failed: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Send consolidated SMS and email for multiple bookings
  const sendConsolidatedNotification = async (bookings, bookingType) => {
    try {
      // Get user details for the notification
      const userPhone = user?.phone || user?.phoneNumber;
      const userEmail = user?.email;
      const userName = user?.name;

      if (!userPhone || !userEmail || !userName) {
        console.warn('❌ Missing user details for consolidated notification');
        return;
      }

      // Calculate total amount from bookings
      const totalAmount = bookings.reduce((sum, booking) => {
        // Try to get amount from different possible fields
        return sum + (booking.totalPrice || booking.amount || 0);
      }, 0);

      // Prepare the data structure that matches the backend DTO
      const consolidatedData = {
        phoneNumber: userPhone.startsWith('+') ? userPhone : `+91${userPhone}`,
        guestName: userName,
        guestEmail: userEmail,
        totalAmount: totalAmount.toString(),
        paymentMethod: bookingType, // "online" or "pay-at-hotel"
        bookings: bookings.map(booking => ({
          roomName: booking.roomName || booking.room?.name || 'Room',
          checkInDate: booking.checkInDate,
          checkOutDate: booking.checkOutDate,
          bookingId: booking.id,
          roomAmount: (booking.totalPrice || booking.amount || 0).toString(),
          guests: booking.guests || 1,
          breakfast: booking.breakfast || false,
          breakfastCount: booking.breakfastCount || 0,
          extraBed: booking.extraBed || false,
          extraBedCount: booking.extraBedCount || 0,
          occupancyType: booking.occupancyType || 'SINGLE'
        }))
      };

      console.log('🔗 Sending consolidated notification with correct structure:', consolidatedData);

      const response = await fetch(getApiUrl('/api/notifications/consolidated'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(consolidatedData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('❌ Failed to send consolidated notification:', errorText);
      } else {
        const result = await response.json();
        console.log('✅ Consolidated notification sent successfully:', result);
      }
    } catch (error) {
      console.error('❌ Error sending consolidated notification:', error);
      // Don't throw error - bookings were still created successfully
    }
  };

  // Function to check and update cart item availability
  const checkCartAvailability = async () => {
    if (cartItems.length === 0) {
      toast.warning('Your cart is empty');
      return;
    }
    
    setIsCheckingAvailability(true);
    const token = localStorage.getItem('token');
    
    try {
      const updatedItems = [];
      const unavailableItems = [];
        for (const item of cartItems) {
        try {
          // Use externalId if available (for DynamoDB rooms), otherwise use regular ID
          let actualRoomId = item.room.externalId || item.room.id.toString();

          const availabilityResponse = await fetch(
            `${getEndpointUrl('ROOMS', 'AVAILABILITY')}?roomId=${actualRoomId}&checkIn=${new Date(item.checkInDate).toISOString().split('T')[0]}&checkOut=${new Date(item.checkOutDate).toISOString().split('T')[0]}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (availabilityResponse.ok) {
            const availability = await availabilityResponse.json();
            if (availability.available) {
              updatedItems.push(item);
            } else {
              unavailableItems.push(item);
            }
          } else {
            // If we can't check, keep the item but warn user
            console.warn('Could not check availability for room:', item.room.name);
            updatedItems.push(item);
          }
        } catch (error) {
          console.error('Error checking availability for item:', item, error);
          // Keep item if error occurs during check
          updatedItems.push(item);
        }
      }
        if (unavailableItems.length > 0) {
        const roomNames = unavailableItems.map(item => `${item.room.name} (${item.checkInDate} to ${item.checkOutDate})`).join(', ');
        
        // Set prominent error display
        const errorItems = unavailableItems.map(item => ({
          roomName: item.room.name,
          checkIn: item.checkInDate,
          checkOut: item.checkOutDate
        }));
        setAvailabilityErrors(errorItems);
        setShowAvailabilityAlert(true);
        
        if (unavailableItems.length === 1) {
          toast.error(`This room is no longer available: ${roomNames}. Please remove it from your cart or choose different dates.`);
        } else {
          toast.error(`These rooms are no longer available: ${roomNames}. Please remove them from your cart or choose different dates.`);        }
        
        // Highlight unavailable items in the UI (this would require additional state management)
        
      } else {
        // Clear any previous errors
        setAvailabilityErrors([]);
        setShowAvailabilityAlert(false);
        
        toast.success(`All ${cartItems.length} item${cartItems.length > 1 ? 's' : ''} in your cart are still available! You can proceed with payment.`);
      }
      
    } catch (error) {
      console.error('Error checking cart availability:', error);
      toast.error('Could not verify room availability. Please try again or contact support.');
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  if (!user) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="cart-empty">
            <h2>Please login to view your cart</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="page-header">
          <h1>Booking Cart</h1>
          <p>Review your selected bookings</p>
        </div>

        {cartItems.length === 0 ? (
          <div className="cart-empty">
            <h2>Your cart is empty</h2>
            <p>Add some room bookings to get started</p>
          </div>        ) : (
          <div className="cart-content">
            {/* Availability Alert */}
            {showAvailabilityAlert && availabilityErrors.length > 0 && (
              <div className="availability-alert">
                <div className="alert-header">
                  <AlertCircle size={24} className="alert-icon" />
                  <h3>⚠️ Room Availability Issue</h3>
                  <button 
                    className="alert-close"
                    onClick={() => setShowAvailabilityAlert(false)}
                    title="Dismiss alert"
                  >
                    ×
                  </button>
                </div>
                <div className="alert-content">
                  <p className="alert-message">
                    {availabilityErrors.length === 1 
                      ? 'The following room is no longer available for your selected dates:'
                      : 'The following rooms are no longer available for your selected dates:'
                    }
                  </p>
                  <ul className="unavailable-rooms-list">
                    {availabilityErrors.map((error, index) => (
                      <li key={index} className="unavailable-room">
                        <strong>{error.roomName}</strong>
                        <span className="dates">({error.checkIn} to {error.checkOut})</span>
                      </li>
                    ))}
                  </ul>
                  <div className="alert-actions">
                    <p>Please remove these rooms from your cart or choose different dates before proceeding with payment.</p>
                    <button 
                      className="check-again-btn"
                      onClick={() => {
                        setShowAvailabilityAlert(false);
                        checkCartAvailability();
                      }}
                    >
                      Check Again
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="cart-items">{cartItems.map((item, index) => (<div key={index} className="cart-item">
                  <div className="item-image">
                    <img 
                      src={
                        (item.room.imageUrl && !item.room.imageUrl.includes('/api/placeholder/')) ? item.room.imageUrl :
                        (item.room.image && !item.room.image.includes('/api/placeholder/')) ? item.room.image :
                        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRTJFOEYwIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2NDc0OEIiPlJvb20gSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo='
                      } 
                      alt={item.room.name}
                      onError={(e) => {
                        console.error('Failed to load cart room image:', item.room.imageUrl || item.room.image);
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRTJFOEYwIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2NDc0OEIiPlJvb20gSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo=';
                      }}
                    />
                  </div>
                  
                  <div className="item-details">
                    <h3>{item.room.name}</h3>
                    <p className="room-size">Size: {item.room.size}</p>
                      <div className="booking-info">
                      <div className="info-row">
                        <Calendar size={16} />
                        <span>
                          {formatDateForDisplay(item.checkInDate)} - {formatDateForDisplay(item.checkOutDate)}
                        </span>
                      </div>
                      <div className="info-row">
                        <Users size={16} />
                        <span>{item.guests} Guest{item.guests > 1 ? 's' : ''}</span>
                      </div>
                      <div className="info-row">
                        <span className="nights">{item.nights} Night{item.nights > 1 ? 's' : ''}</span>
                      </div>
                      {item.occupancyType && (
                        <div className="info-row">
                          <span className="occupancy-type">
                            {item.occupancyType === 'single' ? 'Single' : 'Double'} Occupancy
                          </span>
                        </div>
                      )}
                      {item.extraBedCount > 0 && (
                        <div className="info-row">
                          <span className="extra-beds">
                            {item.extraBedCount} Extra Bed{item.extraBedCount > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      {item.includeBreakfast ? (
                        <div className="info-row">
                          <span className="breakfast-included">
                            ✓ Breakfast Included
                          </span>
                        </div>
                      ) : (
                        <div className="info-row">
                          <span className="rooms-only">
                            🏨 Rooms Only
                          </span>
                        </div>
                      )}
                    </div>                    <div className="item-price">
                      {(() => {
                        const breakdown = calculateItemBreakdown(item);
                        return (
                          <>
                            <div className="price-breakdown">
                              <div className="base-price">
                                {item.occupancyType && item.room.singleOccupancyPrice ? 
                                  `${item.occupancyType === 'single' ? 'Single' : 'Double'}: ₹${breakdown.basePrice}` :
                                  `Room: ₹${breakdown.basePrice}`
                                } × {item.nights} night{item.nights > 1 ? 's' : ''}
                              </div>
                              {item.extraBedCount > 0 && (
                                <div className="extra-bed-price">
                                  Extra bed: ₹{item.room.extraBedPrice} × {item.extraBedCount} × {item.nights} nights = ₹{breakdown.extraBedPrice * item.nights}
                                </div>
                              )}
                              {item.includeBreakfast && item.room.breakfastPrice > 0 && (
                                <div className="breakfast-price">
                                  Breakfast: ₹{item.room.breakfastPrice} × {breakdown.breakfastPeople} person{breakdown.breakfastPeople > 1 ? 's' : ''} × {item.nights} nights = ₹{breakdown.breakfastPrice * item.nights}
                                </div>
                              )}
                              <div className="subtotal">Subtotal: ₹{breakdown.subtotal}</div>
                              <div className="gst">GST (12%): ₹{breakdown.gst}</div>
                            </div>
                            <span className="total-price">Total: ₹{breakdown.total}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <button 
                    className="remove-btn"
                    onClick={() => removeFromCart(index)}
                    title="Remove from cart"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            <div className="cart-summary">
              <div className="summary-card">
                <h3>Booking Summary</h3>
                  <div className="summary-details">
                  <div className="summary-row">
                    <span>Total Bookings:</span>
                    <span>{cartItems.length}</span>
                  </div>
                  <div className="summary-row">
                    <span>Total Nights:</span>
                    <span>{cartItems.reduce((total, item) => total + item.nights, 0)}</span>
                  </div>
                  {(() => {
                    const totalBreakdown = calculateTotalBreakdown();
                    return (
                      <>
                        <div className="summary-row">
                          <span>Subtotal (before GST):</span>
                          <span>₹{totalBreakdown.subtotal}</span>
                        </div>
                        <div className="summary-row">
                          <span>GST (12%):</span>
                          <span>₹{totalBreakdown.gst}</span>
                        </div>
                        <div className="summary-row total">
                          <span>Total Amount (incl. GST):</span>
                          <span>₹{totalBreakdown.total}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                
                <div className="payment-info">
                  <div className="payment-methods-summary">
                    <p>💳 <strong>Accepted:</strong> UPI, Cards, Net Banking, Wallets</p>
                    {isTestMode && <small>🧪 Test Mode: Use test cards or your real Indian card (no charge)</small>}
                  </div>
                </div>
                
                <div className="summary-actions">
                  <button 
                    className="clear-cart-btn"
                    onClick={clearCart}
                    disabled={isProcessing || isCheckingAvailability}
                  >
                    Clear Cart
                  </button>
                    <button 
                    className="check-availability-btn"
                    onClick={checkCartAvailability}
                    disabled={isProcessing || isCheckingAvailability}
                    title="Check if all rooms in your cart are still available"
                  >
                    {isCheckingAvailability ? (
                      <>
                        <div className="loading-spinner"></div>
                        Checking...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Check Availability
                      </>
                    )}
                  </button>

                  <button 
                    className="checkout-btn"
                    onClick={handleCheckout}
                    disabled={isProcessing || isCheckingAvailability}
                  >
                    {isProcessing ? (
                      <>
                        <div className="loading-spinner"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Pay Online
                      </>
                    )}
                  </button>

                  <button 
                    className="pay-at-hotel-btn"
                    onClick={handlePayAtHotel}
                    disabled={isProcessing || isCheckingAvailability}
                  >
                    {isProcessing ? (
                      <>
                        <div className="loading-spinner"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Pay At Hotel
                      </>
                    )}
                  </button>
                  
                  {paymentStatus === 'success' && (
                    <div className="payment-status success">
                      <div className="status-content">
                        <CheckCircle size={16} />
                        <span>
                          {bookingType === 'online' 
                            ? `Payment Successful! ${completedBookings.length} booking(s) confirmed.`
                            : `Booking Request Submitted! ${completedBookings.length} booking(s) pending approval.`
                          }
                        </span>
                      </div>
                      {completedBookings.length > 0 && (
                        <div className="booking-details">
                          {completedBookings.map((booking, index) => (
                            <div key={index} className="booking-summary">
                              {booking.roomName || booking.room?.name || 'Room'} • {booking.checkInDate} to {booking.checkOutDate}
                            </div>
                          ))}
                        </div>
                      )}
                      <Link to="/profile" className="view-bookings-link">
                        {bookingType === 'online' ? 'View All Bookings →' : 'Track Booking Status →'}
                      </Link>
                    </div>
                  )}
                  
                  {paymentStatus === 'failed' && (
                    <div className="payment-status failed">
                      <AlertCircle size={16} />
                      Payment Failed
                    </div>
                  )}
                </div>
              </div>

              <div className="booking-note">
                <h4>Important Notes:</h4>
                <ul>
                  <li>All bookings are subject to availability</li>
                  <li>Check-in time: 12:00 PM</li>
                  <li>Check-out time: 11:00 AM</li>
                  <li>Cancellation policy applies</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
